import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'baex.theme';

function readStored(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark') return value;
  } catch {
    /* storage blocked — fall through to system */
  }
  return 'system';
}

function apply(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

/** Resolves what the viewer is actually looking at right now. */
export function resolvedTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme(): {
  preference: ThemePreference;
  effective: 'light' | 'dark';
  toggle: () => void;
} {
  const [preference, setPreference] = useState<ThemePreference>(() => readStored());

  useEffect(() => {
    apply(preference);
  }, [preference]);

  const toggle = useCallback(() => {
    setPreference((current) => {
      const next: ThemePreference = resolvedTheme(current) === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage blocked — the attribute still applies for this session */
      }
      return next;
    });
  }, []);

  return { preference, effective: resolvedTheme(preference), toggle };
}
